import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query, where } from '../../firebase.config';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import { useTableSort, SortableHeader } from '../../utils/tableSort';
import { useTablePagination } from '../../utils/tablePagination';
import PaginationControls from '../../utils/PaginationControls';
import { formatCurrency } from './CommonComponents';
import GlobalExportButtons from '../GlobalExportButtons';
import { formatPercentage } from '../../utils/numberFormat';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';

const HsnGstSummaryReport = ({ db, userId, appId, companyId, dateRange, selectedParty, parties, loading, setLoading, forcedGstType, companyDetails }) => {
  const [rows, setRows] = useState([]);
  const [viewMode, setViewMode] = useState('both'); // both, sales, purchases
  const gstType = forcedGstType || ((companyDetails?.gstinType || '').toLowerCase() === 'composition' ? 'composition' : 'regular');

  // Auto-set view mode for composition scheme
  useEffect(() => {
    if (gstType === 'composition') {
      setViewMode('sales');
    }
  }, [gstType]);

  useEffect(() => {
    const load = async () => {
      if (!db || !userId || !appId || !companyId) {
        console.log('HsnGstSummaryReport: Missing required props:', { db: !!db, userId, appId, companyId });
        return;
      }
      setLoading(true);
      try {
        const startStr = new Date(dateRange.start).toISOString().split('T')[0];
        const endStr = new Date(dateRange.end).toISOString().split('T')[0];
        const salesCol = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
        const [salesSnapInvoice, salesSnapDate] = await Promise.all([
          getDocs(query(salesCol, where('invoiceDate', '>=', startStr), where('invoiceDate', '<=', endStr), orderBy('invoiceDate', 'desc'))).catch(() => ({ docs: [] })),
          getDocs(query(salesCol, where('date', '>=', startStr), where('date', '<=', endStr), orderBy('date', 'desc'))).catch(() => ({ docs: [] }))
        ]);
        let sales = [...salesSnapInvoice.docs, ...salesSnapDate.docs]
          .reduce((acc, d) => { const id = d.id; if (!acc._seen[id]) { acc._seen[id] = true; acc.arr.push({ id, ...d.data() }); } return acc; }, { _seen: {}, arr: [] }).arr;

        const purchaseCol = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
        const [purchaseSnapBill, purchaseSnapDate] = await Promise.all([
          getDocs(query(purchaseCol, where('billDate', '>=', startStr), where('billDate', '<=', endStr), orderBy('billDate', 'desc'))).catch(() => ({ docs: [] })),
          getDocs(query(purchaseCol, where('date', '>=', startStr), where('date', '<=', endStr), orderBy('date', 'desc'))).catch(() => ({ docs: [] }))
        ]);
        let purchases = [...purchaseSnapBill.docs, ...purchaseSnapDate.docs]
          .reduce((acc, d) => { const id = d.id; if (!acc._seen[id]) { acc._seen[id] = true; acc.arr.push({ id, ...d.data() }); } return acc; }, { _seen: {}, arr: [] }).arr;

        const itemsSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'items'));
        const itemMeta = {};
        itemsSnap.forEach(d => {
          const it = d.data() || {};
          itemMeta[d.id] = { hsn: it.hsnCode || '', gst: parseFloat(it.gstPercentage) || 0, name: it.itemName || '' , itemType: it.itemType || 'Goods', compositionGstRate: parseFloat(it.compositionGstRate) || 0 };
        });
        if (selectedParty) {
          sales = sales.filter(s => (s.party || s.partyId) === selectedParty);
          purchases = purchases.filter(p => (p.party || p.partyId) === selectedParty);
        }

        const groups = new Map(); // key: hsn|rate|type
        const add = (hsn, rate, taxable, cgst, sgst, igst, itemId, itemName, type) => {
          const key = `${hsn}|${rate}|${type}`;
          if (!groups.has(key)) groups.set(key, { hsn, rate, taxable: 0, cgst: 0, sgst: 0, igst: 0, items: new Map(), type });
          const g = groups.get(key);
          g.taxable += taxable; g.cgst += cgst; g.sgst += sgst; g.igst += igst;
          if (itemId) {
            if (!g.items.has(itemId)) g.items.set(itemId, { itemId, name: itemName || '-', taxable: 0, cgst: 0, sgst: 0, igst: 0 });
            const it = g.items.get(itemId);
            it.taxable += taxable; it.cgst += cgst; it.sgst += sgst; it.igst += igst;
          }
        };

        const addFromRows = (billRows, isOutward, type) => {
          const list = Array.isArray(billRows) ? billRows : [];
          for (const r of list) {
            const base = parseFloat(r.amount) || 0;
            const meta = itemMeta[r.item] || {};
            const regularRate = (parseFloat(r.sgst) || 0) + (parseFloat(r.cgst) || 0) + (parseFloat(r.igst) || 0) || meta.gst || 0;
            let compRate = meta.compositionGstRate;
            if (!compRate) compRate = meta?.itemType === 'Service' ? 6 : 1;
            const rate = gstType === 'composition' ? compRate : regularRate;
            const hsn = r.hsn || meta.hsn || '';
            let cg, sg, ig;
            if (gstType === 'composition') {
              const totalGst = (base * (rate || 0)) / 100;
              cg = totalGst / 2;
              sg = totalGst / 2;
              ig = 0;
            } else {
              cg = ((parseFloat(r.cgst) || 0) * base) / 100;
              sg = ((parseFloat(r.sgst) || 0) * base) / 100;
              ig = ((parseFloat(r.igst) || 0) * base) / 100;
            }
            add(hsn, rate, base, cg, sg, ig, r.item, meta.name, type);
          }
        };

        // Process sales (outward supplies)
        if (viewMode === 'both' || viewMode === 'sales') {
          sales.forEach(s => addFromRows(s.rows || s.items, true, 'Sales'));
        }
        
        // Process purchases (inward supplies for ITC) - only for regular scheme
        if (gstType !== 'composition' && (viewMode === 'both' || viewMode === 'purchases')) {
          purchases.forEach(p => addFromRows(p.rows || p.items, false, 'Purchase'));
        }

        const groupsArray = Array.from(groups.values()).map(g => ({
          ...g,
          items: Array.from(g.items.values())
        }));
        setRows(groupsArray);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [db, userId, appId, companyId, dateRange, selectedParty, viewMode]);

  const { sortedData, sortConfig, handleSort } = useTableSort(rows, { key: 'hsn', direction: 'asc' });
  const pagination = useTablePagination(sortedData, 25);

  // Quick totals computed from filtered/sorted data (not just current page)
  const quickTotals = useMemo(() => {
    const base = sortedData;
    const taxable = base.reduce((s, r) => s + (parseFloat(r.taxable) || 0), 0);
    const cgst = base.reduce((s, r) => s + (parseFloat(r.cgst) || 0), 0);
    const sgst = base.reduce((s, r) => s + (parseFloat(r.sgst) || 0), 0);
    const igst = base.reduce((s, r) => s + (parseFloat(r.igst) || 0), 0);
    const totalGST = cgst + sgst + (gstType === 'regular' ? igst : 0);
    
    // Additional breakdowns for regular scheme when showing both
    let salesBreakdown = null;
    let purchasesBreakdown = null;
    
    if (gstType === 'regular' && viewMode === 'both') {
      const sales = base.filter(r => r.type === 'Sales');
      const purchases = base.filter(r => r.type === 'Purchase');
      
      salesBreakdown = {
        taxable: sales.reduce((s, r) => s + (parseFloat(r.taxable) || 0), 0),
        cgst: sales.reduce((s, r) => s + (parseFloat(r.cgst) || 0), 0),
        sgst: sales.reduce((s, r) => s + (parseFloat(r.sgst) || 0), 0),
        igst: sales.reduce((s, r) => s + (parseFloat(r.igst) || 0), 0),
        totalGST: sales.reduce((s, r) => s + (parseFloat(r.cgst) || 0) + (parseFloat(r.sgst) || 0) + (parseFloat(r.igst) || 0), 0)
      };
      
      purchasesBreakdown = {
        taxable: purchases.reduce((s, r) => s + (parseFloat(r.taxable) || 0), 0),
        cgst: purchases.reduce((s, r) => s + (parseFloat(r.cgst) || 0), 0),
        sgst: purchases.reduce((s, r) => s + (parseFloat(r.sgst) || 0), 0),
        igst: purchases.reduce((s, r) => s + (parseFloat(r.igst) || 0), 0),
        totalGST: purchases.reduce((s, r) => s + (parseFloat(r.cgst) || 0) + (parseFloat(r.sgst) || 0) + (parseFloat(r.igst) || 0), 0)
      };
    }
    
    return { 
      taxable, 
      cgst, 
      sgst, 
      igst, 
      totalGST, 
      groups: base.length,
      salesBreakdown,
      purchasesBreakdown
    };
  }, [sortedData, gstType, viewMode]);


  const buildExportRows = () => {
    const rows = [];
    sortedData.forEach(g => {
      const totalGST = (g.cgst||0)+(g.sgst||0)+(gstType==='regular'?(g.igst||0):0);
      
      // Build row data
      const rowData = { 
        hsn: g.hsn || '-', 
        item: (g.items?.length>1?'Multiple items':(g.items?.[0]?.name||'-')), 
        rate: formatPercentage(g.rate||0), 
        taxable: g.taxable, 
        cgst: g.cgst, 
        sgst: g.sgst, 
        igst: gstType==='regular'?g.igst:0, 
        total: totalGST 
      };
      
      // Add type column for regular scheme when showing both
      if (gstType === 'regular' && viewMode === 'both') {
        rowData.type = g.type || '-';
      }
      
      rows.push(rowData);
      
      if (Array.isArray(g.items) && g.items.length > 1) {
        g.items.forEach(it => {
          const subRowData = { 
            _isSub:true, 
            hsn:'', 
            item: it.name, 
            rate: formatPercentage(g.rate||0), 
            taxable: it.taxable, 
            cgst: it.cgst, 
            sgst: it.sgst, 
            igst: gstType==='regular'?it.igst:0, 
            total: (it.cgst||0)+(it.sgst||0)+(gstType==='regular'?(it.igst||0):0) 
          };
          
          // Add type column for regular scheme when showing both
          if (gstType === 'regular' && viewMode === 'both') {
            subRowData.type = '';
          }
          
          rows.push(subRowData);
        });
      }
    });
    
    // Totals (only parent rows)
    const totals = rows.filter(r=>!r._isSub).reduce((acc,r)=>({
      taxable: acc.taxable + (parseFloat(r.taxable)||0),
      cgst: acc.cgst + (parseFloat(r.cgst)||0),
      sgst: acc.sgst + (parseFloat(r.sgst)||0),
      igst: acc.igst + (parseFloat(r.igst)||0),
      total: acc.total + (parseFloat(r.total)||0)
    }), {taxable:0,cgst:0,sgst:0,igst:0,total:0});
    
    const totalRow = { hsn:'', item:'TOTAL', rate:'', ...totals };
    
    // Add type column for regular scheme when showing both
    if (gstType === 'regular' && viewMode === 'both') {
      totalRow.type = '';
    }
    
    rows.push(totalRow);
    return rows;
  };

  // Prepare export data for GlobalExportButtons
  const getExportData = () => buildExportRows();

  const getExportColumns = () => {
    const cols = [
      { key: 'hsn', label: 'HSN Code' },
      { key: 'item', label: 'Item' }
    ];
    
    // Add type column for regular scheme when showing both
    if (gstType === 'regular' && viewMode === 'both') {
      cols.push({ key: 'type', label: 'Type' });
    }
    
    cols.push(
      { key: 'rate', label: 'GST Rate %' },
      { key: 'taxable', label: 'Taxable Amount' },
      { key: 'cgst', label: 'CGST' },
      { key: 'sgst', label: 'SGST' }
    );
    
    if (gstType === 'regular') {
      cols.push({ key: 'igst', label: 'IGST' });
    }
    
    cols.push({ key: 'total', label: 'Total GST' });
    
    return cols;
  };

  const getReportDetails = () => ({
    'Period': `${new Date(dateRange.start).toLocaleDateString('en-IN')} to ${new Date(dateRange.end).toLocaleDateString('en-IN')}`,
    'GST Type': gstType === 'regular' ? 'Regular' : 'Composition',
    'View Mode': gstType === 'regular' ? (viewMode === 'both' ? 'Sales + Purchases' : viewMode === 'sales' ? 'Sales Only' : 'Purchases Only') : 'Sales Only',
    'Total Taxable': quickTotals.taxable,
    'Total CGST': quickTotals.cgst,
    'Total SGST': quickTotals.sgst,
    'Total IGST': quickTotals.igst,
    'Total GST': quickTotals.totalGST,
    dateRange
  });

  return (
    <div className="p-3 sm:p-6">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold text-gray-800 mb-2">HSN-wise GST Summary ({gstType === 'regular' ? 'Regular' : 'Composition'})</h2>
          <p className="text-gray-600">Period: {new Date(dateRange.start).toLocaleDateString('en-IN')} to {new Date(dateRange.end).toLocaleDateString('en-IN')}</p>
          {gstType === 'composition' && (
            <p className="text-sm text-blue-600 mt-1">Note: Composition scheme only considers sales for tax calculation</p>
          )}
          {gstType === 'regular' && (
            <p className="text-sm text-gray-600 mt-1">
              {viewMode === 'both' && 'Showing combined data for both sales and purchases'}
              {viewMode === 'sales' && 'Showing only sales data'}
              {viewMode === 'purchases' && 'Showing only purchases data'}
            </p>
          )}
        </div>
        <div className="w-full sm:w-auto shrink-0">
        <GlobalExportButtons
          data={getExportData()}
          columns={getExportColumns()}
          filename={`HSN_GST_${gstType === 'regular' ? viewMode.toUpperCase() : 'COMPOSITION'}`}
          title="HSN-wise GST Summary"
          companyDetails={companyDetails}
          reportDetails={getReportDetails()}
          disabled={rows.length === 0}
        />
        </div>
      </div>

      {/* View Mode Selector - Only for Regular Scheme */}
      {gstType === 'regular' && (
        <div className="mb-6">
          <div className="flex items-center space-x-4">
            <label className="block text-sm font-medium text-gray-700">View Mode:</label>
            <AppSelect
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              aria-label="View Mode"
              sheetTitle="View Mode"
              options={[
                { value: 'both', label: 'Sales + Purchases (Combined)' },
                { value: 'sales', label: 'Sales Only' },
                { value: 'purchases', label: 'Purchases Only' },
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} w-auto`}
            />
            <span className="text-sm text-gray-600">
              {viewMode === 'both' && 'Showing combined data for both sales and purchases'}
              {viewMode === 'sales' && 'Showing only sales data'}
              {viewMode === 'purchases' && 'Showing only purchases data'}
            </span>
          </div>
        </div>
      )}

      {/* Quick Totals */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${gstType==='regular' ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-4 mb-6`}>
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-blue-800">Groups</div>
          <div className="text-2xl font-semibold text-blue-900">{quickTotals.groups}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-green-800">Total Taxable</div>
          <div className="text-2xl font-semibold text-green-900">{formatCurrency(quickTotals.taxable)}</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-sm text-purple-800">Total GST</div>
          <div className="text-2xl font-semibold text-purple-900">{formatCurrency(quickTotals.totalGST)}</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="text-sm text-yellow-800">CGST</div>
          <div className="text-2xl font-semibold text-yellow-900">{formatCurrency(quickTotals.cgst)}</div>
        </div>
        <div className="bg-pink-50 p-4 rounded-lg">
          <div className="text-sm text-pink-800">SGST</div>
          <div className="text-2xl font-semibold text-pink-900">{formatCurrency(quickTotals.sgst)}</div>
        </div>
        {gstType === 'regular' && (
          <div className="bg-indigo-50 p-4 rounded-lg">
            <div className="text-sm text-indigo-800">IGST</div>
            <div className="text-2xl font-semibold text-indigo-900">{formatCurrency(quickTotals.igst)}</div>
          </div>
        )}
      </div>

      {/* Additional Breakdown for Regular Scheme when showing both */}
      {gstType === 'regular' && viewMode === 'both' && quickTotals.salesBreakdown && quickTotals.purchasesBreakdown && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Sales vs Purchases Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sales Summary */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-green-800 mb-3">Sales Summary</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="text-sm">
                  <span className="text-gray-600">Taxable:</span>
                  <span className="ml-2 font-medium text-green-800">{formatCurrency(quickTotals.salesBreakdown.taxable)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">CGST:</span>
                  <span className="ml-2 font-medium text-green-800">{formatCurrency(quickTotals.salesBreakdown.cgst)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">SGST:</span>
                  <span className="ml-2 font-medium text-green-800">{formatCurrency(quickTotals.salesBreakdown.sgst)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">IGST:</span>
                  <span className="ml-2 font-medium text-green-800">{formatCurrency(quickTotals.salesBreakdown.igst)}</span>
                </div>
                <div className="text-sm col-span-2">
                  <span className="text-gray-600">Total GST:</span>
                  <span className="ml-2 font-medium text-green-800">{formatCurrency(quickTotals.salesBreakdown.totalGST)}</span>
                </div>
              </div>
            </div>
            
            {/* Purchases Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-blue-800 mb-3">Purchases Summary</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="text-sm">
                  <span className="text-gray-600">Taxable:</span>
                  <span className="ml-2 font-medium text-blue-800">{formatCurrency(quickTotals.purchasesBreakdown.taxable)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">CGST:</span>
                  <span className="ml-2 font-medium text-blue-800">{formatCurrency(quickTotals.purchasesBreakdown.cgst)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">SGST:</span>
                  <span className="ml-2 font-medium text-blue-800">{formatCurrency(quickTotals.purchasesBreakdown.sgst)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">IGST:</span>
                  <span className="ml-2 font-medium text-blue-800">{formatCurrency(quickTotals.purchasesBreakdown.igst)}</span>
                </div>
                <div className="text-sm col-span-2">
                  <span className="text-gray-600">Total GST:</span>
                  <span className="ml-2 font-medium text-blue-800">{formatCurrency(quickTotals.purchasesBreakdown.totalGST)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader columnKey="hsn" label="HSN" onSort={handleSort} sortConfig={sortConfig} />
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
              {gstType === 'regular' && viewMode === 'both' && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              )}
              <SortableHeader columnKey="rate" label="GST %" onSort={handleSort} sortConfig={sortConfig} />
              <SortableHeader columnKey="taxable" label="Taxable" onSort={handleSort} sortConfig={sortConfig} />
              <SortableHeader columnKey="cgst" label="CGST" onSort={handleSort} sortConfig={sortConfig} />
              <SortableHeader columnKey="sgst" label="SGST" onSort={handleSort} sortConfig={sortConfig} />
              {gstType === 'regular' && (
                <SortableHeader columnKey="igst" label="IGST" onSort={handleSort} sortConfig={sortConfig} />
              )}
              <SortableHeader columnKey="total" label="Total GST" onSort={handleSort} sortConfig={sortConfig} />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pagination.currentData.map((g, i) => {
              const totalGST = (g.cgst || 0) + (g.sgst || 0) + (g.igst || 0);
              const hasMultiple = (g.items || []).length > 1;
              return (
                <React.Fragment key={g.hsn + '|' + g.rate + '|' + i}>
                  <tr>
                    <td className="px-6 py-3">{g.hsn || '-'}</td>
                    <td className="px-6 py-3">{hasMultiple ? 'Multiple items' : (g.items?.[0]?.name || '-')}</td>
                    {gstType === 'regular' && viewMode === 'both' && (
                      <td className="px-6 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          g.type === 'Sales' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {g.type || '-'}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-3 text-right">{formatPercentage(g.rate || 0)}</td>
                    <td className="px-6 py-3 text-right">{formatCurrency(g.taxable)}</td>
                    <td className="px-6 py-3 text-right">{formatCurrency(g.cgst)}</td>
                    <td className="px-6 py-3 text-right">{formatCurrency(g.sgst)}</td>
                    {gstType === 'regular' && (
                      <td className="px-6 py-3 text-right">{formatCurrency(g.igst)}</td>
                    )}
                    <td className="px-6 py-3 text-right">{formatCurrency(totalGST)}</td>
                  </tr>
                  {hasMultiple && g.items.map((it, idx) => (
                    <tr key={g.hsn + '|' + g.rate + '|item|' + idx} className="bg-gray-50">
                      <td className="px-6 py-2 text-xs text-gray-500"></td>
                      <td className="px-6 py-2 text-xs text-gray-700">{it.name}</td>
                      {gstType === 'regular' && viewMode === 'both' && (
                        <td className="px-6 py-2 text-xs text-gray-500"></td>
                      )}
                      <td className="px-6 py-2 text-xs text-right text-gray-600">{formatPercentage(g.rate || 0)}</td>
                      <td className="px-6 py-2 text-xs text-right text-gray-600">{formatCurrency(it.taxable)}</td>
                      <td className="px-6 py-2 text-xs text-right text-gray-600">{formatCurrency(it.cgst)}</td>
                      <td className="px-6 py-2 text-xs text-right text-gray-600">{formatCurrency(it.sgst)}</td>
                      {gstType === 'regular' && (
                        <td className="px-6 py-2 text-xs text-right text-gray-600">{formatCurrency(it.igst)}</td>
                      )}
                      <td className="px-6 py-2 text-xs text-right text-gray-600">{formatCurrency((it.cgst||0)+(it.sgst||0)+(it.igst||0))}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <PaginationControls {...pagination} />
      </div>
    </div>
  );
};

export default HsnGstSummaryReport;


